import React, { useEffect, useState } from "react";
import { DownloadSimple, CheckCircle, DeviceMobile, X, Export, DotsThreeVertical } from "@phosphor-icons/react";
import { brandName } from "../brand";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function platform() {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

export default function InstallApp({ compact = false }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    const mq = window.matchMedia?.("(display-mode: standalone)");
    const onMq = () => setInstalled(isStandalone());
    mq?.addEventListener?.("change", onMq);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      mq?.removeEventListener?.("change", onMq);
    };
  }, []);

  const handleClick = async () => {
    if (installed) return;
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice.catch(() => null);
      if (choice?.outcome === "accepted") setInstalled(true);
      setDeferred(null);
      return;
    }
    setShowModal(true); // no native prompt available -> instructions
  };

  const p = platform();
  let label = deferred ? `Install ${brandName}` : "Add to Home Screen";
  if (installed) label = "App Installed";

  return (
    <>
      <button
        onClick={handleClick}
        disabled={installed}
        data-testid="install-app-btn"
        data-install-state={installed ? "installed" : deferred ? "installable" : "instructions"}
        className={`inline-flex items-center justify-center gap-2 rounded-sm px-4 py-2.5 text-sm font-semibold transition-colors ${
          installed
            ? "cursor-default border border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
            : "bg-ff-gold text-ff-bg hover:bg-ff-brass"
        } ${compact ? "w-auto" : "w-full"}`}
      >
        {installed ? <CheckCircle size={18} weight="fill" /> : <DownloadSimple size={18} weight="bold" />}
        {label}
      </button>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowModal(false)} data-testid="install-instructions-modal">
          <div className="ff-panel w-full max-w-sm rounded-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-ff-gold"><DeviceMobile size={20} weight="duotone" /><h3 className="font-display text-lg font-semibold">Add {brandName}</h3></div>
              <button onClick={() => setShowModal(false)} className="text-ff-muted hover:text-ff-parchment" data-testid="install-modal-close"><X size={20} /></button>
            </div>
            <p className="mb-3 text-sm text-ff-muted">Aurumward is a web app — you can add it to your home screen for a full-screen, app-like experience. No file is downloaded.</p>

            {p === "ios" && (
              <div className="rounded-sm border border-ff-brass/20 bg-ff-bg/50 p-3 text-sm text-ff-parchment" data-testid="install-ios">
                <p className="mb-1 font-semibold text-ff-brass">iPhone / iPad (Safari)</p>
                <p className="flex items-center gap-1.5">Tap <Export size={16} className="text-ff-gold" /> Share, then choose <b>“Add to Home Screen.”</b></p>
              </div>
            )}
            {p === "android" && (
              <div className="rounded-sm border border-ff-brass/20 bg-ff-bg/50 p-3 text-sm text-ff-parchment" data-testid="install-android">
                <p className="mb-1 font-semibold text-ff-brass">Android (Chrome)</p>
                <p className="flex items-center gap-1.5">Open the browser menu <DotsThreeVertical size={16} className="text-ff-gold" /> and choose <b>“Install app”</b> or <b>“Add to Home screen.”</b></p>
              </div>
            )}
            {p === "desktop" && (
              <div className="rounded-sm border border-ff-brass/20 bg-ff-bg/50 p-3 text-sm text-ff-parchment" data-testid="install-desktop">
                <p className="mb-1 font-semibold text-ff-brass">Desktop</p>
                <p>Look for the install icon in your browser’s address bar, or open the browser menu and choose <b>“Install Aurumward.”</b></p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
